import { useCallback, useEffect, useMemo, useState } from "react";
import {
  defaultDateFilter,
  type DefaultDateFilterParams,
  type PerformanceWithAccountFilter,
} from "../utils/performanceUtils";
import { useReportingAccountType } from "components/reportingFilter/ReportingFilterRequests";

type Props = DefaultDateFilterParams & {
  accountId: number;
  supportsSPType?: boolean;
  // Optional overrides to apply to the initial filter (and on reset)
  initialOverrides?: Partial<PerformanceWithAccountFilter>;
};

export const usePerformanceFilter = (
  props: Props,
  publisherId?: number,
): [PerformanceWithAccountFilter, (filter: PerformanceWithAccountFilter) => void, () => void] => {
  const {
    includeDistinct,
    accountFilterShouldShow,
    includeAccountsDropdown,
    includeVideoType,
    includePriority,
    includeStatus,
    includePacks,
    accountId,
    initialDateRange,
    supportsSPType,
    initialOverrides,
  } = props;
  const accountType = useReportingAccountType(accountId, supportsSPType, publisherId);

  // Memoize the initial filter to prevent unnecessary recreations
  const initialFilter = useMemo(
    () => ({
      ...defaultDateFilter({
        includeDistinct,
        accountFilterShouldShow,
        includeAccountsDropdown,
        includeVideoType,
        accountId,
        initialDateRange,
        includePriority,
        includeStatus,
        includePacks,
      }),
      type: accountType,
      ...(initialOverrides ?? {}),
    }),
    [
      includeDistinct,
      accountFilterShouldShow,
      includeAccountsDropdown,
      includeVideoType,
      accountId,
      initialDateRange,
      accountType,
      includePriority,
      includeStatus,
      includePacks,
      initialOverrides,
    ],
  );

  const [filter, setFilter] = useState<PerformanceWithAccountFilter>(() => initialFilter);

  const resetFilter = useCallback(() => {
    // Only run if accountType is defined (not loading)
    if (accountType === undefined) return;
    setFilter(initialFilter);
  }, [initialFilter, accountType]);

  useEffect(() => {
    // Only run resetFilter when accountType is ready
    if (accountType !== undefined) {
      resetFilter();
    }
  }, [resetFilter, accountType]);

  return [filter, setFilter, resetFilter] as const;
};
